import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaBriefcase, FaSearch, FaPhone, FaMapMarkerAlt, FaPlus, FaFilter
} from 'react-icons/fa';
import './ProbationParoleOffices.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const ProbationParoleOffices = () => {
  const [offices, setOffices] = useState([]);
  const [filterOptions, setFilterOptions] = useState({
    counties: [],
    regions: []
  });
  const [filters, setFilters] = useState({
    search: '',
    county: '',
    region: ''
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchFilterOptions();
    fetchOffices();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchOffices();
    }, 300);

    return () => clearTimeout(timer);
  }, [filters]);

  const fetchFilterOptions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/probation-parole/filters/options`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success) {
        setFilterOptions(data.data);
      }
    } catch (err) {
      console.error('Error fetching filter options:', err);
    }
  };

  const fetchOffices = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      // Build query string
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.county) params.append('county', filters.county);
      if (filters.region) params.append('region', filters.region);

      const response = await fetch(`${API_URL}/probation-parole?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success) {
        setOffices(data.data);
        setError('');
      } else {
        setError('Failed to load probation & parole offices');
      }
    } catch (err) {
      console.error('Error fetching offices:', err);
      setError('Failed to load probation & parole offices');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      county: '',
      region: ''
    });
  };

  const handleOfficeClick = (officeId) => {
    navigate(`/address-book/probation-parole/${officeId}`);
  };

  const getOfficeTypeBadge = (officeType) => {
    if (!officeType) return null;

    const colors = {
      'Adult': '#2c3e50',
      'Federal': '#8e44ad',
      'Juvenile/Youth Court': '#16a085'
    };

    return (
      <span
        className="office-type-badge"
        style={{ backgroundColor: colors[officeType] || '#7f8c8d' }}
      >
        {officeType}
      </span>
    );
  };

  if (loading && offices.length === 0) {
    return (
      <div className="probation-parole-container">
        <div className="loading">Loading probation & parole offices...</div>
      </div>
    );
  }

  return (
    <div className="probation-parole-container">
      {/* Header */}
      <div className="probation-parole-header">
        <div className="header-left">
          <FaBriefcase size={32} />
          <div>
            <h1>Probation & Parole Offices</h1>
            <p className="subtitle">{offices.length} offices found</p>
          </div>
        </div>
        <button className="btn-add-office" onClick={() => navigate('/address-book/probation-parole/new')}>
          <FaPlus /> Add Office
        </button>
      </div>

      {/* Filters */}
      <div className="probation-parole-filters">
        <div className="search-box">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search by name or city..."
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-row">
          <div className="filter-group">
            <label><FaFilter /> County</label>
            <select
              value={filters.county}
              onChange={(e) => handleFilterChange('county', e.target.value)}
              className="filter-select"
            >
              <option value="">All Counties</option>
              {filterOptions.counties.map(county => (
                <option key={county} value={county}>{county}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label><FaFilter /> Region</label>
            <select
              value={filters.region}
              onChange={(e) => handleFilterChange('region', e.target.value)}
              className="filter-select"
            >
              <option value="">All Regions</option>
              {filterOptions.regions.map(region => (
                <option key={region} value={region}>{region}</option>
              ))}
            </select>
          </div>

          {(filters.search || filters.county || filters.region) && (
            <button className="btn-clear-filters" onClick={clearFilters}>
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {/* Offices List */}
      <div className="offices-list">
        {offices.length === 0 ? (
          <div className="no-results">
            <FaBriefcase size={48} />
            <p>No probation & parole offices found matching your criteria</p>
          </div>
        ) : (
          offices.map(office => (
            <div
              key={office.id}
              className="office-card"
              onClick={() => handleOfficeClick(office.id)}
            >
              <div className="office-header">
                <div className="office-title">
                  <FaBriefcase className="office-icon" />
                  <h3>{office.name}</h3>
                </div>
                <div className="office-badges">
                  {getOfficeTypeBadge(office.office_type)}
                  {office.region && (
                    <span className="region-badge">{office.region}</span>
                  )}
                </div>
              </div>

              <div className="office-info">
                <div className="info-row">
                  {(office.address_city || office.city) && (
                    <div className="info-item">
                      <FaMapMarkerAlt className="info-icon" />
                      <span>{office.address_city || office.city}, MT</span>
                    </div>
                  )}

                  {office.phone && (
                    <div className="info-item">
                      <FaPhone className="info-icon" />
                      <span>{office.phone}</span>
                    </div>
                  )}
                </div>

                {office.address_street && (
                  <div className="address-detail">
                    {office.address_street}
                  </div>
                )}

                {office.service_area && (
                  <div className="service-area">
                    Service Area: {office.service_area}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ProbationParoleOffices;
